import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

/**
 * Live clock that ticks every second in the given IANA timezone.
 * Uses Intl.DateTimeFormat so DST transitions are handled automatically —
 * you never have to worry about EST vs EDT, BST vs GMT, etc.
 *
 * Rendered under the Agent Timezone dropdown so the user can visually confirm
 * what "now" looks like for the agent. Also reassures that the AI has live
 * access to this exact value via the `{{current_datetime}}` dynamic variable.
 */
const LiveTimezoneClock = ({ timezone, textPrimary = 'text-white', textSecondary = 'text-white/60', cardBorder = 'border-white/10' }) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  let formatted = '';
  let tzLabel = '';
  try {
    formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(now);

    // Get the short tz abbreviation (e.g. "EDT", "EST") — shown separately so
    // the full spoken form is what the AI uses but the user still sees standard tz.
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(now);
    tzLabel = parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch (e) {
    formatted = 'Invalid timezone';
  }

  return (
    <div className={`mt-4 p-4 rounded-xl border ${cardBorder} bg-teal-400/5`} data-testid="agent-timezone-live-clock">
      <div className="flex items-center gap-2 mb-2">
        <Clock size={14} className="text-teal-400" />
        <span className={`text-xs uppercase tracking-wider ${textSecondary}`}>Current time in this timezone</span>
      </div>
      <div className={`text-lg font-mono tabular-nums ${textPrimary}`}>
        {formatted}
        {tzLabel && <span className={`ml-2 text-sm ${textSecondary}`}>({tzLabel})</span>}
      </div>
      <p className={`text-xs mt-2 ${textSecondary}`}>
        AI has live access to this exact date &amp; time on every call. DST is handled automatically.
      </p>
    </div>
  );
};

export default LiveTimezoneClock;
